import { db } from '../../firebase';
import {
  doc,
  updateDoc,
  writeBatch,
  query,
  where,
  getDocs,
  collection,
} from 'firebase/firestore';

const K_FIRST_MATCHES = 80;
const K_AFTER = 40;

function calculateElo(currentElo, opponentElo, score, matchCount) {
  const expected = 1 / (1 + Math.pow(10, (opponentElo - currentElo) / 400));
  const K = matchCount < 8 ? K_FIRST_MATCHES : K_AFTER;
  return Math.round(currentElo + K * (score - expected));
}

export async function updateEloFromMatch(match, tournamentId, force = false) {
  try {
    if (match.eloUpdated && !force) return;

    if (
      match?.teamA?.score === undefined ||
      match?.teamB?.score === undefined
    ) return;

    const allPlayers = [
      ...(match.teamAPlayers || []),
      ...(match.teamBPlayers || [])
    ].filter(p => typeof p.phone === 'string');

    if (allPlayers.length !== 4) {
      console.warn('⛔ Joueurs manquants ou incomplets dans ce match :', match.id || 'match sans id');
      return;
    }

    const batch = writeBatch(db);
    const deltas = {};

    const getPlayerDoc = async (phone) => {
      const q = query(collection(db, 'joueurs'), where('phone', '==', phone));
      const snap = await getDocs(q);
      return !snap.empty ? snap.docs[0] : null;
    };

    const docsA = await Promise.all(match.teamAPlayers.map(p => getPlayerDoc(p.phone)));
    const docsB = await Promise.all(match.teamBPlayers.map(p => getPlayerDoc(p.phone)));

    if (docsA.includes(null) || docsB.includes(null)) {
      console.warn("🚫 Impossible de trouver tous les joueurs en base");
      return;
    }

    const playersA = docsA.map(doc => ({ id: doc.id, ...doc.data() }));
    const playersB = docsB.map(doc => ({ id: doc.id, ...doc.data() }));

    const eloA = ((playersA[0].elo ?? 700) + (playersA[1].elo ?? 700)) / 2;
    const eloB = ((playersB[0].elo ?? 700) + (playersB[1].elo ?? 700)) / 2;
    const matchCountA = ((playersA[0].matchCount ?? 0) + (playersA[1].matchCount ?? 0)) / 2;
    const matchCountB = ((playersB[0].matchCount ?? 0) + (playersB[1].matchCount ?? 0)) / 2;

    const scoreA = match.teamA.score > match.teamB.score ? 1 :
                   match.teamA.score < match.teamB.score ? 0 : 0.5;
    const scoreB = 1 - scoreA;

    const newEloA = calculateElo(eloA, eloB, scoreA, matchCountA);
    const newEloB = calculateElo(eloB, eloA, scoreB, matchCountB);

    const deltaA = newEloA - eloA;
    const deltaB = newEloB - eloB;

    for (let i = 0; i < 2; i++) {
      const docA = docsA[i];
      const docB = docsB[i];
      const pA = playersA[i];
      const pB = playersB[i];

      batch.update(doc(db, 'joueurs', docA.id), {
        elo: (pA.elo ?? 700) + deltaA,
        matchCount: (pA.matchCount ?? 0) + 1
      });
      batch.update(doc(db, 'joueurs', docB.id), {
        elo: (pB.elo ?? 700) + deltaB,
        matchCount: (pB.matchCount ?? 0) + 1
      });

      deltas[pA.phone] = deltaA;
      deltas[pB.phone] = deltaB;
    }

    const basePath = match.source === 'league' ? 'league_matches' : 'tournaments';
    const matchRef = doc(db, `${basePath}/${tournamentId}/${match.path}/${match.id}`);
    batch.update(matchRef, { eloUpdated: true });

    await batch.commit();
    return deltas;

  } catch (error) {
    console.error("❌ Erreur dans updateEloFromMatch:", error);
  }
}
